/*
 * Dirt Masters — "Fits My Vehicle" vehicle search + collection filter (gap I).
 *
 * Two custom elements share this file (and the ONE dmSlug implementation):
 *
 *   <dm-ymm-search>    sections/dm-ymm-search.liquid — the primary YMM search
 *                      widget: a type-ahead ("2007 ford f" -> "2007 Ford
 *                      F-150") over the vehicle index, plus Year -> Make ->
 *                      Model cascading dropdowns, plus "Find parts"
 *                      navigating to the landing collection with the vehicle
 *                      filter applied.
 *
 *   <dm-fits-vehicle>  snippets/dm-fits-vehicle.liquid — the collection-page
 *                      bar: the same Year -> Make -> Model cascade, NARROWED
 *                      to the vehicles actually present in the current
 *                      collection (available fit_models facet values are
 *                      embedded by the snippet), + an apply/clear toggle that
 *                      preserves every other active facet. A Trucks page
 *                      offers truck makes, not Arctic Cat.
 *
 * Data: the YMM tree v2 shop metafield (custom.ymm_tree), embedded by the
 * rendering Liquid as an application/json script tag (data-dm-tree):
 *   {"v":2,"makes":{"Polaris":{"RZR XP 1000":[2014,2015,...]}}}
 * Contract: docs/fits-my-vehicle-collection-filter-spec.md. The version check
 * is mandatory — any v !== 2 renders the no-vehicle-data state (element hides).
 *
 * Filtering is Shopify-native and server-side, via URL params:
 *   filter.p.m.custom.fit_models=slug(make + " " + model)
 *   filter.p.m.custom.fit_years=<year>
 * Never client-side hiding: collections paginate server-side at 24/page, so a
 * browser-side filter would miss matches on later pages and break counts.
 *
 * Saved vehicle: localStorage key "dm_vehicle" =
 *   {"v":1,"make":"Polaris","model":"RZR XP 1000","year":2019}
 * Display names exactly as they appear as tree keys; slugs are always derived
 * via dmSlug at use time, never stored. Both elements read and write the same
 * key; storage writes happen only on an APPLY action (Find parts / Show parts
 * that fit) — browsing the pickers is not a commitment.
 */

/* The shared slug() — must match the inventory's segment_tags.slug and the
 * spec's golden vectors (spec §A.2; tested by scripts/test_slug.mjs). Order
 * matters: trim, lowercase, THEN collapse non-alphanumeric runs. */
function dmSlug(s) {
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

(function () {
  const STORAGE_KEY = 'dm_vehicle';
  const PARAM_MODELS = 'filter.p.m.custom.fit_models';
  const PARAM_YEARS = 'filter.p.m.custom.fit_years';
  const MODEL_CAP = 12; // direct model boxes before drill-down kicks in
  const LIST_CAP = 18; // expanded list when the query is a mid-name qualifier
  const GROUP_CAP = 18; // family/make boxes (compact, still no scrolling)
  const REMAINDER_CAP = 3; // gapped-recall leaves appended after group boxes

  /* ---- vehicle index ---------------------------------------------------- */

  function readJson(host, selector) {
    try {
      const el = host.querySelector(selector);
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  }

  function readTree(host) {
    const tree = readJson(host, 'script[type="application/json"][data-dm-tree]');
    // Contract v2 only — refuse other versions rather than guess.
    if (!tree || tree.v !== 2 || typeof tree.makes !== 'object' || tree.makes === null) return null;
    if (Object.keys(tree.makes).length === 0) return null;
    return tree;
  }

  /* Flatten the tree to entries, optionally narrowed to a set of available
   * composite slugs (the current collection's fit_models facet values). */
  function buildIndex(tree, availableSlugs) {
    const entries = [];
    Object.keys(tree.makes).forEach((make) => {
      Object.keys(tree.makes[make]).forEach((model) => {
        const years = tree.makes[make][model];
        if (!Array.isArray(years) || years.length === 0) return;
        const slug = dmSlug(make + ' ' + model);
        if (availableSlugs && !availableSlugs.has(slug)) return;
        entries.push({
          make: make,
          model: model,
          years: years,
          slug: slug,
          text: (make + ' ' + model).toLowerCase(),
        });
      });
    });
    return entries;
  }

  function yearsOf(entries) {
    const set = new Set();
    entries.forEach((e) => e.years.forEach((y) => set.add(y)));
    return Array.from(set).sort((a, b) => b - a); // newest first
  }

  function makesForYear(entries, year) {
    const set = new Set();
    entries.forEach((e) => {
      if (e.years.indexOf(year) !== -1) set.add(e.make);
    });
    return Array.from(set).sort();
  }

  function modelsForYearMake(entries, year, make) {
    return entries
      .filter((e) => e.make === make && e.years.indexOf(year) !== -1)
      .map((e) => e.model)
      .sort();
  }

  function validVehicle(entries, make, model, year) {
    const y = Number(year);
    return entries.some((e) => e.make === make && e.model === model && e.years.indexOf(y) !== -1);
  }

  /* ---- type-ahead matching ---------------------------------------------- */

  /* "2007 ford f" -> {year: 2007, terms: [...], lastActive: true}.
   * COMPLETED terms (everything except a still-being-typed last token) match
   * exact word boundaries; only the ACTIVE token is prefix-fuzzy — so a
   * selected "RZR " excludes RZR4 while typing "rzr" still suggests both.
   * Trailing whitespace marks the last token completed (refine-clicks append
   * one); stray terminal punctuation (iOS double-space period, autocorrect
   * commas) is stripped so it can't smudge the boundary. */
  function parseQuery(q) {
    const lastActive = !/\s$/.test(q);
    const tokens = q
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.replace(/[.,;]+$/, ''))
      .filter(Boolean);
    let year = null;
    const terms = [];
    tokens.forEach((t) => {
      if (year === null && /^(19|20)\d{2}$/.test(t)) {
        year = Number(t);
      } else {
        terms.push(t);
      }
    });
    return { year: year, terms: terms, lastActive: lastActive && terms.length > 0 };
  }

  /* Ranked ordered matching over the "make model" words (joint design with
   * GPT-5.6, 2026-08-06): terms must match IN ORDER (gaps allowed, so
   * "rzr 1000" finds "RZR XP 1000"); COMPLETED terms need exact word
   * equality, the ACTIVE last term matches by prefix. Consecutive runs and
   * early anchors score higher; a scattered any-order match survives only as
   * a low-ranked fallback. Returns -1 for no match. */
  function matchEntry(entry, terms, lastActive) {
    const words = entry.text.split(/[^a-z0-9]+/).filter(Boolean);
    const termMatches = (term, word, isActive) =>
      isActive ? word.indexOf(term) === 0 : word === term;

    // Ordered (gaps allowed): greedy left-to-right.
    let wi = 0;
    let score = 0;
    let prev = -2;
    let ordered = true;
    for (let ti = 0; ti < terms.length; ti++) {
      const isActive = lastActive && ti === terms.length - 1;
      let found = -1;
      for (let k = wi; k < words.length; k++) {
        if (termMatches(terms[ti], words[k], isActive)) {
          found = k;
          break;
        }
      }
      if (found === -1) {
        ordered = false;
        break;
      }
      score += found === prev + 1 ? 4 : 2; // consecutive beats gapped
      if (ti === 0 && found === 0) score += 2; // anchored at the front
      prev = found;
      wi = found + 1;
    }
    if (ordered) return score;

    // Scattered fallback: all terms match SOME word, any order. Low rank.
    for (let ti = 0; ti < terms.length; ti++) {
      const isActive = lastActive && ti === terms.length - 1;
      if (!words.some((w) => termMatches(terms[ti], w, isActive))) return -1;
    }
    return 1;
  }

  /* STRICT drill membership (joint design round 2 w/ GPT-5.6: "P is
   * discovery; A is navigation"): an entry is ANCHORED when the query terms
   * first consume make words in-order-exact (all or nothing — a partial make
   * match is not anchored), and every remaining term aligns to the MODEL
   * words consecutively FROM WORD 0 (completed=exact, active last=prefix).
   * Anchored entries are what group boxes may claim; gapped matches stay
   * discoverable as leaf results but never shape or join a group. Returns
   * null (not anchored) or {modelConsumed} = completed-aligned model words. */
  function anchoredEntry(entry, terms, lastActive) {
    const makeWords = entry.make.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const modelWords = entry.model.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    let ti = 0;
    let mi = 0;
    while (ti < terms.length && mi < makeWords.length) {
      const isActive = lastActive && ti === terms.length - 1;
      if (!isActive && makeWords[mi] === terms[ti]) {
        ti++;
        mi++;
        continue;
      }
      if (isActive && makeWords[mi].indexOf(terms[ti]) === 0) {
        return { modelConsumed: 0 }; // still typing the make itself
      }
      break;
    }
    if (mi > 0 && mi < makeWords.length) return null; // partial make: not anchored
    let k = 0;
    for (; ti < terms.length; ti++, k++) {
      if (k >= modelWords.length) return null;
      const isActive = lastActive && ti === terms.length - 1;
      const w = modelWords[k];
      const t = terms[ti];
      if (isActive ? w.indexOf(t) !== 0 : w !== t) return null;
    }
    return { modelConsumed: lastActive && k > 0 ? k - 1 : k };
  }

  function suggest(entries, query) {
    const q = parseQuery(query);
    if (q.year === null && q.terms.length === 0) return [];
    const out = [];
    for (const entry of entries) {
      if (q.year !== null && entry.years.indexOf(q.year) === -1) continue;
      const score = q.terms.length ? matchEntry(entry, q.terms, q.lastActive) : 0;
      if (score < 0) continue;
      out.push({ entry: entry, year: q.year, score: score });
    }
    out.sort((a, b) => b.score - a.score || a.entry.text.localeCompare(b.entry.text));

    if (out.length <= MODEL_CAP) return out;

    // ANCHORED-A / REMAINDER-R: only anchored entries may form or join group
    // boxes; gapped qualifier hits ("turbo" mid-name) stay visible as leaf
    // results and are never silently discarded by a refine.
    const A = [];
    const R = [];
    let modelConsumed = 0;
    for (const s2 of out) {
      const a = anchoredEntry(s2.entry, q.terms, q.lastActive);
      if (a) {
        A.push(s2);
        modelConsumed = a.modelConsumed; // identical across A by construction
      } else {
        R.push(s2);
      }
    }

    if (A.length === 0) return out.slice(0, LIST_CAP); // pure qualifier query
    if (A.length <= MODEL_CAP) return A.concat(R).slice(0, LIST_CAP);

    const makes = new Set(A.map((s2) => s2.entry.make));
    const grouped =
      makes.size === 1
        ? groupByModelWords(A, q.year, modelConsumed)
        : groupByMake(A, q.year);
    if (grouped) return grouped.concat(R.slice(0, REMAINDER_CAP));
    return A.concat(R).slice(0, LIST_CAP);
  }

  /* Family drill-down over the ANCHORED pool: group by the first k model
   * words, starting one past the words the query has already consumed (so a
   * clicked "RZR" refine groups at word 2: RZR 900 / RZR Pro / RZR XP...).
   * A depth whose grouping cannot split advances — the no-progress
   * invariant; nothing ever repeats. */
  function groupByModelWords(scored, year, modelConsumed) {
    const pinned = modelConsumed;
    for (let depth = pinned + 1; depth <= pinned + 4; depth++) {
      const groups = new Map();
      scored.forEach((s) => {
        const key = s.entry.model.split(/\s+/).slice(0, depth).join(' ');
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(s);
      });
      if (groups.size === 1) continue; // no split at this depth — go deeper
      const out = [];
      groups.forEach((members, key) => {
        if (members.length === 1) {
          out.push(members[0]);
        } else {
          out.push({
            group: key,
            refine: members[0].entry.make + ' ' + key,
            count: members.length,
            year: year,
          });
        }
      });
      out.sort(
        (a, b) =>
          (b.count || 1) - (a.count || 1) ||
          (a.group || a.entry.model).localeCompare(b.group || b.entry.model)
      );
      return out.slice(0, GROUP_CAP);
    }
    return null; // never splits — caller falls back to the capped model list
  }

  /* Mixed-make overflow (e.g. a bare year): one refine-box per make. */
  function groupByMake(scored, year) {
    const groups = new Map();
    scored.forEach((s) => {
      if (!groups.has(s.entry.make)) groups.set(s.entry.make, []);
      groups.get(s.entry.make).push(s);
    });
    const out = [];
    groups.forEach((members, make) => {
      if (members.length === 1) out.push(members[0]);
      else out.push({ group: make, refine: make, count: members.length, year: year });
    });
    out.sort(
      (a, b) =>
        (b.count || 1) - (a.count || 1) ||
        (a.group || a.entry.model).localeCompare(b.group || b.entry.model)
    );
    return out.slice(0, GROUP_CAP);
  }

  /* ---- storage ----------------------------------------------------------- */

  function readSaved(entries) {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const v = raw ? JSON.parse(raw) : null;
      if (!v || v.v !== 1) return null;
      return validVehicle(entries, v.make, v.model, v.year)
        ? { make: v.make, model: v.model, year: Number(v.year) }
        : null;
    } catch (e) {
      return null;
    }
  }

  function saveVehicle(make, model, year) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ v: 1, make: make, model: model, year: Number(year) })
      );
    } catch (e) {
      /* storage unavailable: pickers still work for this page view */
    }
  }

  function clearSaved() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* storage unavailable: nothing to clear */
    }
  }

  /* Tokens for the fold-migration comparison. The fold preserves spelling and
   * order, but the gated normalize_fitment_spellings step DID rewrite casing on
   * some rows (the Commander MAX / Maverick Max collisions), so compare
   * case-insensitively. */
  function modelTokens(model) {
    return String(model)
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
  }

  /* Is `a` a subsequence of `b` — same order, gaps allowed? */
  function isSubsequence(a, b) {
    let i = 0;
    for (let j = 0; j < b.length && i < a.length; j++) {
      if (b[j] === a[i]) i++;
    }
    return i === a.length;
  }

  /* Garage migration for the 2026-08-29 fit_models family fold (spec §B.7).
   * Saved vehicles hold PRE-FOLD display names ("General 1000 EPS Premium").
   * The fold only DELETES whitespace-separated tokens, so the vehicle's new
   * rung is the LONGEST tree model whose tokens are a subsequence of the saved
   * model's. Two rules from the contract:
   *   - the resolved entry must also carry the saved YEAR (validVehicle checks
   *     make + model + year, so a model-only match is not a resolution);
   *   - a length TIE is NOT a match. "Ranger 1000 Crew EPS" subsequence-matches
   *     both "Ranger 1000" and "Ranger Crew"; picking one silently puts the
   *     shopper on the wrong vehicle and every later filter inherits it. Drop
   *     and let them re-select instead.
   * MUST be given the FULL index: buildIndex() is narrowed to the current
   * collection's facet values on collection pages, and migrating against a
   * narrowed set would rewrite storage to whatever that one page happens to
   * carry. Idempotent — a current saved vehicle is left untouched.
   * Returns the vehicle it CLEARED (for the status line), else null. */
  function migrateSaved(fullEntries) {
    let raw = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
    if (!raw) return null;

    let v = null;
    try {
      v = JSON.parse(raw);
    } catch (e) {
      clearSaved();
      return null;
    }
    if (!v || v.v !== 1 || !v.make || !v.model) {
      clearSaved();
      return null;
    }

    const year = Number(v.year);
    if (validVehicle(fullEntries, v.make, v.model, year)) return null;

    const savedTokens = modelTokens(v.model);
    let best = null;
    let bestLen = 0;
    let tied = false;
    fullEntries.forEach((e) => {
      if (e.make !== v.make || e.years.indexOf(year) === -1) return;
      const t = modelTokens(e.model);
      if (t.length === 0 || t.length > savedTokens.length) return;
      if (!isSubsequence(t, savedTokens)) return;
      if (t.length > bestLen) {
        best = e;
        bestLen = t.length;
        tied = false;
      } else if (t.length === bestLen && best && e.model !== best.model) {
        tied = true;
      }
    });

    if (best && !tied) {
      saveVehicle(best.make, best.model, year);
      return null;
    }
    clearSaved();
    return { make: v.make, model: v.model, year: year };
  }

  /* ---- select + URL helpers ---------------------------------------------- */

  function fillSelect(select, values, placeholder) {
    select.textContent = '';
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = placeholder;
    select.appendChild(opt);
    values.forEach((value) => {
      const o = document.createElement('option');
      o.value = String(value);
      o.textContent = String(value);
      select.appendChild(o);
    });
  }

  function filterUrl(basePath, search, vehicle) {
    const params = new URLSearchParams(search);
    params.delete(PARAM_MODELS);
    params.delete(PARAM_YEARS);
    params.delete('page');
    if (vehicle) {
      params.set(PARAM_MODELS, dmSlug(vehicle.make + ' ' + vehicle.model));
      params.set(PARAM_YEARS, String(vehicle.year));
    }
    const query = params.toString();
    return basePath + (query ? '?' + query : '');
  }

  /* ---- shared cascade (Year -> Make -> Model), used by both elements ----- */

  class DMVehicleCascade {
    /* host exposes: entries, yearSelect, makeSelect, modelSelect,
     * onSelectionChange(). */
    constructor(host) {
      this.host = host;
      const years = yearsOf(host.entries);
      fillSelect(host.yearSelect, years, 'Year');
      host.yearSelect.disabled = years.length === 0;
      host.yearSelect.addEventListener('change', () => {
        this.onYear();
        host.onSelectionChange();
      });
      host.makeSelect.addEventListener('change', () => {
        this.onMake();
        host.onSelectionChange();
      });
      host.modelSelect.addEventListener('change', () => host.onSelectionChange());
    }

    onYear() {
      const host = this.host;
      const year = Number(host.yearSelect.value);
      if (year) {
        fillSelect(host.makeSelect, makesForYear(host.entries, year), 'Make');
        host.makeSelect.disabled = false;
      } else {
        fillSelect(host.makeSelect, [], 'Make');
        host.makeSelect.disabled = true;
      }
      fillSelect(host.modelSelect, [], 'Model');
      host.modelSelect.disabled = true;
    }

    onMake() {
      const host = this.host;
      const year = Number(host.yearSelect.value);
      const make = host.makeSelect.value;
      if (year && make) {
        fillSelect(host.modelSelect, modelsForYearMake(host.entries, year, make), 'Model');
        host.modelSelect.disabled = false;
      } else {
        fillSelect(host.modelSelect, [], 'Model');
        host.modelSelect.disabled = true;
      }
    }

    /* Set the controls to a known-valid complete vehicle. */
    adopt(vehicle) {
      const host = this.host;
      host.yearSelect.value = String(vehicle.year);
      this.onYear();
      host.makeSelect.value = vehicle.make;
      this.onMake();
      host.modelSelect.value = vehicle.model;
      host.onSelectionChange();
    }

    selection() {
      const host = this.host;
      const year = host.yearSelect.value;
      const make = host.makeSelect.value;
      const model = host.modelSelect.value;
      if (!year || !make || !model || !validVehicle(host.entries, make, model, year)) return null;
      return { make: make, model: model, year: Number(year) };
    }
  }

  /* ---- <dm-ymm-search> — the hero search widget -------------------------- */

  class DMYmmSearch extends HTMLElement {
    connectedCallback() {
      const tree = readTree(this);
      if (!tree) {
        this.hidden = true;
        return;
      }
      this.entries = buildIndex(tree, null);
      migrateSaved(this.entries); // fold migration, spec §B.7 — full index

      this.yearSelect = this.querySelector('[data-dm-year]');
      this.makeSelect = this.querySelector('[data-dm-make]');
      this.modelSelect = this.querySelector('[data-dm-model]');
      this.findButton = this.querySelector('[data-dm-find]');
      this.input = this.querySelector('[data-dm-typeahead]');
      this.listbox = this.querySelector('[data-dm-suggestions]');
      if (!this.yearSelect || !this.makeSelect || !this.modelSelect || !this.findButton) return;

      this.landingUrl = this.dataset.landingUrl || '/collections/all';
      this.cascade = new DMVehicleCascade(this);
      this.pendingEntry = null;

      // A year-less typeahead pick parks the pair here; completing Year adopts
      // it (the cascade's own year handler resets make/model first, so this
      // listener — registered after the cascade's — restores the pair).
      this.yearSelect.addEventListener('change', () => {
        const pending = this.pendingEntry;
        const year = Number(this.yearSelect.value);
        if (pending && year && pending.years.indexOf(year) !== -1) {
          this.cascade.adopt({ make: pending.make, model: pending.model, year: year });
        }
      });
      // Touching Make/Model manually abandons the parked pair.
      this.makeSelect.addEventListener('change', () => (this.pendingEntry = null));
      this.modelSelect.addEventListener('change', () => (this.pendingEntry = null));

      const saved = readSaved(this.entries);
      if (saved) this.cascade.adopt(saved);

      this.findButton.addEventListener('click', () => this.onFind());
      if (this.input && this.listbox) this.initTypeahead();
      this.onSelectionChange();
    }

    onSelectionChange() {
      this.findButton.disabled = !this.cascade.selection();
    }

    onFind() {
      const v = this.cascade.selection();
      if (!v) return;
      saveVehicle(v.make, v.model, v.year);
      window.location.assign(filterUrl(this.landingUrl, '', v));
    }

    /* -- type-ahead: input + ARIA listbox -- */

    initTypeahead() {
      this.activeIndex = -1;
      this.suggestions = [];
      this.input.addEventListener('input', () => this.onQuery());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('blur', () => {
        // Delay so a click on an option lands before the list closes.
        window.setTimeout(() => this.closeList(), 150);
      });
      // pointerdown (not click): fires before the input's blur on mouse AND
      // touch, so the list can't close under the tap.
      this.listbox.addEventListener('pointerdown', (e) => {
        const li = e.target.closest('[data-index]');
        if (li) {
          e.preventDefault();
          this.pick(Number(li.dataset.index));
        }
      });
    }

    onQuery() {
      const q = this.input.value;
      this.suggestions = q.trim().length >= 2 ? suggest(this.entries, q) : [];
      this.activeIndex = -1;
      this.renderList();
    }

    renderList() {
      this.listbox.textContent = '';
      const idBase = this.listbox.id || 'dm-ymms-suggestions';
      this.suggestions.forEach((s, i) => {
        const li = document.createElement('li');
        li.id = idBase + '-opt-' + i;
        li.setAttribute('role', 'option');
        li.dataset.index = String(i);
        li.className = 'dm-ymms__option' + (i === this.activeIndex ? ' is-active' : '');
        li.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');
        if (s.group) {
          li.classList.add('dm-ymms__option--group');
          li.textContent = (s.year ? s.year + ' ' : '') + s.group;
          const badge = document.createElement('span');
          badge.className = 'dm-ymms__option-count';
          badge.textContent = s.count + ' \u203a';
          li.appendChild(badge);
        } else {
          li.textContent = s.year
            ? s.year + ' ' + s.entry.make + ' ' + s.entry.model
            : s.entry.make + ' ' + s.entry.model;
        }
        this.listbox.appendChild(li);
      });
      // Typed enough for a search but matched nothing: say so, politely.
      if (this.suggestions.length === 0 && this.input.value.trim().length >= 2) {
        const li = document.createElement('li');
        li.className = 'dm-ymms__option dm-ymms__option--empty';
        li.setAttribute('role', 'presentation');
        li.textContent = 'No matching vehicles';
        this.listbox.appendChild(li);
      }
      const open = this.listbox.childElementCount > 0;
      this.listbox.hidden = !open;
      this.input.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this.activeIndex >= 0) {
        this.input.setAttribute('aria-activedescendant', idBase + '-opt-' + this.activeIndex);
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKey(e) {
      if (this.listbox.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const n = this.suggestions.length;
        this.activeIndex = (this.activeIndex + delta + n) % n;
        this.renderList();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // Only a deliberately highlighted option navigates — a bare Enter on a
        // broad query must never redirect. Single result: safe to take.
        if (this.activeIndex >= 0) {
          this.pick(this.activeIndex);
        } else if (this.suggestions.length === 1) {
          this.pick(0);
        }
      } else if (e.key === 'Escape') {
        this.closeList();
      }
    }

    closeList() {
      this.suggestions = [];
      this.activeIndex = -1;
      this.renderList();
    }

    pick(index) {
      const s = this.suggestions[index];
      if (!s) return;
      if (s.group) {
        // Refine, don't navigate: the box narrows the query one level.
        this.input.value = (s.year ? s.year + ' ' : '') + s.refine + ' ';
        this.input.focus();
        this.onQuery();
        return;
      }
      const entry = s.entry;
      if (s.year) {
        // Complete vehicle: reflect it in the cascade and go straight to parts.
        this.pendingEntry = null;
        this.cascade.adopt({ make: entry.make, model: entry.model, year: s.year });
        this.input.value = s.year + ' ' + entry.make + ' ' + entry.model;
        this.closeList();
        this.onFind();
        return;
      }
      // Pair without a year: park it (the year-change listener adopts it),
      // prefill the controls, hand focus to Year.
      this.pendingEntry = entry;
      fillSelect(this.yearSelect, entry.years.slice().reverse(), 'Year');
      this.yearSelect.disabled = false;
      fillSelect(this.makeSelect, [entry.make], 'Make');
      this.makeSelect.value = entry.make;
      this.makeSelect.disabled = false;
      fillSelect(this.modelSelect, [entry.model], 'Model');
      this.modelSelect.value = entry.model;
      this.modelSelect.disabled = false;
      this.input.value = entry.make + ' ' + entry.model;
      this.closeList();
      this.onSelectionChange();
      this.yearSelect.focus();
    }
  }

  /* ---- <dm-fits-vehicle> — the collection-page bar ----------------------- */

  class DMFitsVehicle extends HTMLElement {
    connectedCallback() {
      const tree = readTree(this);
      if (!tree) {
        this.hidden = true;
        return;
      }

      // Narrow to the current collection's vehicles when the snippet embedded
      // the available fit_models facet values (Trucks page -> truck makes).
      // Two deliberate rules:
      // 1. With a vehicle filter ACTIVE the shopper is in switching context —
      //    other pairs' availability is computed against the active year and
      //    would collapse the picker, so use the full tree instead.
      // 2. Availability present but EMPTY (and nothing active) = this
      //    collection has no fit-filterable products — hide the bar rather
      //    than offer the whole catalog's vehicles (the old sloppy fallback).
      const activeParams0 = new URLSearchParams(window.location.search);
      const vehicleActive = activeParams0.has(PARAM_MODELS) || activeParams0.has(PARAM_YEARS);
      const available = readJson(this, 'script[type="application/json"][data-dm-available]');
      if (!vehicleActive && Array.isArray(available) && available.length === 0) {
        this.hidden = true;
        return;
      }
      const availableSet =
        !vehicleActive && Array.isArray(available) && available.length > 0
          ? new Set(available)
          : null;
      // Migrate BEFORE narrowing: buildIndex(tree, availableSet) is scoped to
      // this collection's facet values, and resolving against it would rewrite
      // storage to whatever this one page carries.
      this.clearedVehicle = migrateSaved(buildIndex(tree, null));
      this.entries = buildIndex(tree, availableSet);

      this.yearSelect = this.querySelector('[data-dm-year]');
      this.makeSelect = this.querySelector('[data-dm-make]');
      this.modelSelect = this.querySelector('[data-dm-model]');
      this.toggleButton = this.querySelector('[data-dm-toggle]');
      this.statusEl = this.querySelector('[data-dm-status]');
      if (!this.yearSelect || !this.makeSelect || !this.modelSelect || !this.toggleButton) return;

      this.cascade = new DMVehicleCascade(this);

      // Selection precedence: an active vehicle filter in the URL wins (shared
      // links reflect reality), else the saved vehicle. URL-adopted vehicles
      // are shown but NOT persisted — only an apply press writes storage.
      const urlVehicle = this.vehicleFromUrl();
      const saved = urlVehicle || readSaved(this.entries);
      if (saved) this.cascade.adopt(saved);

      this.urlVehicle = urlVehicle;
      const activeParams = new URLSearchParams(window.location.search);
      this.filterActive = activeParams.has(PARAM_MODELS) || activeParams.has(PARAM_YEARS);

      this.toggleButton.addEventListener('click', () => this.onToggle());
      this.onSelectionChange();
    }

    /* Adopt only a complete, valid single model + single year pair. Partial
     * or multi-value states still count as an active filter so the bar offers
     * "Clear vehicle filter" — they just don't masquerade as a vehicle. */
    vehicleFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const slugs = params.getAll(PARAM_MODELS);
      const years = params.getAll(PARAM_YEARS);
      if (slugs.length !== 1 || years.length !== 1) return null;
      const year = Number(years[0]);
      const entry = this.entries.find((e) => e.slug === slugs[0]);
      if (!entry || entry.years.indexOf(year) === -1) return null;
      return { make: entry.make, model: entry.model, year: year };
    }

    selectionMatchesActive() {
      const v = this.cascade.selection();
      return Boolean(
        this.filterActive &&
          v &&
          this.urlVehicle &&
          this.urlVehicle.make === v.make &&
          this.urlVehicle.model === v.model &&
          this.urlVehicle.year === v.year
      );
    }

    onToggle() {
      const v = this.cascade.selection();
      // Active + unchanged selection (or no complete selection) -> clear.
      // Active + a DIFFERENT picked vehicle -> re-apply for the new vehicle.
      if (this.filterActive && (!v || this.selectionMatchesActive())) {
        this.navigate(null);
        return;
      }
      if (!v) return;
      saveVehicle(v.make, v.model, v.year);
      this.navigate(v);
    }

    /* Server-side filtering: rewrite only our two params (+ page) on the
     * current URL, preserving every other active facet and the sort order. */
    navigate(vehicle) {
      window.location.assign(filterUrl(window.location.pathname, window.location.search, vehicle));
    }

    onSelectionChange() {
      const v = this.cascade.selection();
      const clearing = this.filterActive && (!v || this.selectionMatchesActive());
      this.toggleButton.disabled = !this.filterActive && !v;
      this.toggleButton.setAttribute('aria-pressed', clearing ? 'true' : 'false');
      this.classList.toggle('dm-fitsv--active', this.filterActive);
      this.toggleButton.textContent = clearing ? 'Clear vehicle filter' : 'Show parts that fit';
      if (this.statusEl) {
        const a = this.urlVehicle;
        if (this.filterActive && a) {
          this.statusEl.textContent =
            'Showing parts that fit your ' + a.year + ' ' + a.make + ' ' + a.model + '.';
        } else if (this.filterActive) {
          this.statusEl.textContent = 'Showing parts that fit your vehicle.';
        } else if (v) {
          this.statusEl.textContent = 'Saved vehicle: ' + v.year + ' ' + v.make + ' ' + v.model;
        } else if (this.clearedVehicle) {
          this.statusEl.textContent =
            'Your saved ' +
            this.clearedVehicle.year +
            ' ' +
            this.clearedVehicle.make +
            ' ' +
            this.clearedVehicle.model +
            ' is no longer one of our listed models — please pick your vehicle again.';
        } else {
          this.statusEl.textContent = '';
        }
      }
    }
  }

  if (!customElements.get('dm-ymm-search')) {
    customElements.define('dm-ymm-search', DMYmmSearch);
  }
  if (!customElements.get('dm-fits-vehicle')) {
    customElements.define('dm-fits-vehicle', DMFitsVehicle);
  }

  /* Test hook (node only — see scripts/test_typeahead.mjs); inert in browsers. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      dmSlug: dmSlug,
      parseQuery: parseQuery,
      matchEntry: matchEntry,
      suggest: suggest,
      buildIndex: buildIndex,
      validVehicle: validVehicle,
      isSubsequence: isSubsequence,
      modelTokens: modelTokens,
      migrateSaved: migrateSaved,
      yearsOf: yearsOf,
      makesForYear: makesForYear,
      modelsForYearMake: modelsForYearMake,
    };
  }
})();
